import React from "react";
import { InfoOutlined, HighlightOffOutlined } from "@mui/icons-material";
import { IconButton, Popover, Stack, Typography, styled } from "@mui/material";
import { format, formatDuration, parseISO } from "date-fns";
import * as durationFn from "duration-fns";

import { getDateFnsLocale } from "@/lib/date-fns-locale";
import { useTranslation, useLocale } from "@/lib/i18n";
import type { AppLocale } from "@/lib/locale/resolve-effective-locale";
import { darkGrey } from "@/theme/colors";
import { LegacyWidgetConfiguration, Widget } from "@/lib/api";
import { useWidget } from "@/features/dashboard/features/widgets";
import { AdditionalWidgetData } from "@/types/additional-widget-data";
import { Serie } from "@/types/serie";
import { Renderer } from "@/types/widget";
import { Html } from "@/components/html/html";

type Props = {
  widget: Widget;
};

const StyledPopover = styled(Popover)(({ theme }) => ({
  "& .MuiPopover-paper": {
    padding: theme.spacing(3),
    backgroundColor: "#000000CC",
    maxWidth: 450,
    minWidth: 300,
    display: "flex",
    flexDirection: "row",
    alignItems: "flex-start",

    "& .MuiTypography-root": {
      color: "white",
    },
  },
}));

const formatDateToLocale = (date: string | undefined, locale: AppLocale) => {
  if (!date) return null;

  const parsedDate = parseISO(date);

  return format(parsedDate, "d LLLL yyyy H:mm", {
    locale: getDateFnsLocale(locale),
  });
};

const RENDERER_WITH_METADATA: Renderer[] = [
  "timeseries",
  "probabilities",
  "wind",
  "textcode",
  "sight",
  "seiches",
  "radar",
  "report",
];

const formatDurationToLocale = (
  duration: string | object | undefined,
  locale: AppLocale,
) => {
  try {
    if (!duration) return null;

    const isDurationObject = typeof duration === "object";
    const parsedDuration = isDurationObject ? duration : durationFn.parse(duration);

    return formatDuration(parsedDuration, {
      locale: getDateFnsLocale(locale),
    });
  } catch {
    return null;
  }
};

const Description = ({ widgetData }: { widgetData?: AdditionalWidgetData }) => {
  const { t } = useTranslation();
  if (!widgetData) return null;

  const { meta } = widgetData;

  if (!meta?.description) return null;

  return (
    <div>
      <Typography fontWeight="bold" variant="h5" mb={1}>
        {t("widgets.meta.general")}
      </Typography>
      <Typography variant="body2">
        <Html content={meta.description} />
      </Typography>
    </div>
  );
};

const MetaLines = ({ widgetData }: { widgetData?: AdditionalWidgetData }) => {
  const { t } = useTranslation();
  const locale = useLocale();
  if (!widgetData) return null;

  const { data } = widgetData;

  if (!data?.length) return null;

  return (
    <div>
      <Typography fontWeight="bold" variant="h5" mb={1}>
        {t("widgets.meta.sources")}
      </Typography>
      <Stack spacing={2}>
        {data.map((product, idx) => (
          <MetaLineItem key={idx} product={product} locale={locale} />
        ))}
      </Stack>
    </div>
  );
};

const MetaLineItem = ({ product, locale }: { product: Serie; locale: AppLocale }) => {
  const { t } = useTranslation();
  const {
    createdAt,
    validUntil,
    updateFrequency,
    source,
    description,
    translatedName,
    name,
  } = product;

  const localizedCreatedAt = formatDateToLocale(createdAt, locale);
  const localizedValidUntil = formatDateToLocale(validUntil, locale);
  const localizedUpateFrequency = formatDurationToLocale(updateFrequency, locale);

  const createdAtString =
    localizedCreatedAt && t("widgets.meta.createdOn", { date: localizedCreatedAt });
  const validUntilString =
    localizedValidUntil && t("widgets.meta.validUntil", { date: localizedValidUntil });
  const updateFrequencyString =
    localizedUpateFrequency &&
    t("widgets.meta.updatesEvery", { duration: localizedUpateFrequency });
  const sourceString = source && t("widgets.meta.source", { source });

  const metaStrings = [
    description,
    createdAtString,
    validUntilString,
    sourceString,
    updateFrequencyString,
  ].filter(Boolean);
  const displayName = translatedName || name;

  if (!metaStrings.length) return;

  return (
    <Stack spacing={1}>
      <Typography sx={{ fontSize: "1.15rem" }}>{displayName}</Typography>
      <Typography variant="body2">{metaStrings.join(". ")}</Typography>
    </Stack>
  );
};

const WidgetInfo = ({ widget: widgetFromProps }: Props) => {
  const [anchorEl, setAnchorEl] = React.useState<HTMLButtonElement | null>(null);

  const widget = useWidget(widgetFromProps);
  const widgetData = widget?.data;

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);
  const id = open ? "widget-info-popover" : undefined;

  const rendererHasMetaData = RENDERER_WITH_METADATA.includes(
    (widgetFromProps.configuration as LegacyWidgetConfiguration).renderer as Renderer,
  );

  if (!rendererHasMetaData) return null;

  return (
    <>
      <IconButton onClick={handleClick}>
        <InfoOutlined fontSize="small" htmlColor={darkGrey} />
      </IconButton>
      <StyledPopover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 25,
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
      >
        <Stack spacing={3}>
          <Description widgetData={widgetData?.data as unknown as AdditionalWidgetData} />
          <MetaLines widgetData={widgetData?.data as unknown as AdditionalWidgetData} />
        </Stack>
        <IconButton onClick={handleClose}>
          <HighlightOffOutlined fontSize="small" htmlColor="white" />
        </IconButton>
      </StyledPopover>
    </>
  );
};

export default WidgetInfo;
